// src/components/FloatingContactButtons.jsx
import React, { useState } from "react";
import { FaWhatsapp, FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import { sendWhatsAppMessage, sendEmailMessage } from "../utils/contactHelpers";

// Email Provider Modal (unchanged)
const EmailProviderModal = ({ isOpen, onClose, onSend, loading }) => {
  if (!isOpen) return null;

  const providers = [
    { id: "gmail", name: "Gmail", icon: "📧", color: "bg-red-500 hover:bg-red-600" },
    { id: "outlook", name: "Outlook", icon: "📨", color: "bg-blue-500 hover:bg-blue-600" },
    { id: "yahoo", name: "Yahoo", icon: "📩", color: "bg-purple-500 hover:bg-purple-600" },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="text-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Choose Email Provider</h3>
          <p className="text-sm text-gray-600">Select your preferred email service.</p>
        </div>
        <div className="space-y-3">
          {providers.map((provider) => (
            <button
              key={provider.id}
              onClick={() => onSend(provider.id)}
              disabled={loading}
              className={`${provider.color} w-full text-white font-semibold py-3 px-4 rounded-md transition flex items-center justify-center gap-2 text-sm disabled:opacity-50`}
            >
              <span>{provider.icon}</span>
              {provider.name}
            </button>
          ))}
          <button
            onClick={onClose}
            className="w-full bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-md hover:bg-gray-300 transition text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const FloatingContactButtons = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [whatsappMsg, setWhatsappMsg] = useState("");
  const [emailMsg, setEmailMsg] = useState("");

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const businessPhone = process.env.REACT_APP_BUSINESS_PHONE || "27681247577";
  const displayPhone = businessPhone.replace(/(\d{3})(\d{2})(\d{3})(\d{4})/, '+$1 $2 $3 $4');

  const handleWhatsAppSend = () => {
    if (!whatsappMsg.trim()) return;
    const fullMessage = `Hello SPK Academy & Publishing,\n\n${whatsappMsg.trim()}`;
    sendWhatsAppMessage(fullMessage);
    setWhatsappMsg("");
  };

  const handleEmailSend = () => {
    if (!emailMsg.trim()) return;
    const message = `Message:\n${emailMsg.trim()}`;
    setPendingMessage(message);
    setShowEmailModal(true);
  };

  const handleEmailProviderSend = (provider) => {
    setLoading(true);
    sendEmailMessage(pendingMessage, provider);
    setLoading(false);
    setShowEmailModal(false);
    setPendingMessage("");
    setEmailMsg("");
  };

  return (
    <>
      {/* FLOATING CONTACT ICONS - Overlap by default, expand on hover */}
      <div
        className={`fixed bottom-10 right-2 sm:bottom-4 sm:right-4 z-50 rounded p-1 flex flex-col sm:flex-row ${isExpanded ? 'bg-red-200 gap-4' : ''
          }`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* WhatsApp */}
        <div className={`relative group ${isExpanded ? '' : 'sm:right-6 sm:-mr-10 sm:-mt-0 -mt-8 sm:mb-0'}`}>
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center text-white text-2xl shadow-xl hover:scale-110 transition-transform duration-200 cursor-pointer">
            <FaWhatsapp size={28} />
          </div>
          <div className="absolute bottom-full right-10 sm:right-0 mb-0 sm:mb-2 w-64 sm:w-72 bg-white rounded-2xl shadow-2xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 origin-bottom-right">
            <h4 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
              <span className="text-green-500"><FaWhatsapp size={16} /></span> WhatsApp Chat
            </h4>
            <p className="text-xs text-gray-500 mb-2">
              Let us know who you are and how we can assist you?
            </p>
            <textarea
              value={whatsappMsg}
              onChange={(e) => setWhatsappMsg(e.target.value)}
              placeholder="Hi, my name is... I'd like to know about..."
              rows="3"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none resize-none"
            />
            <button
              onClick={handleWhatsAppSend}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-2 rounded-lg mt-2 transition text-sm"
            >
              Send via WhatsApp
            </button>
          </div>
        </div>

        {/* Email */}
        <div className={`relative group ${isExpanded ? '' : 'sm:right-6 sm:-mr-10 -mt-8 sm:mt-0'}`}>
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl shadow-xl hover:scale-110 transition-transform duration-200 cursor-pointer">
            <FaEnvelope size={24} />
          </div>
          <div className="absolute bottom-full right-10 sm:right-0 mb-0 sm:mb-2 w-64 sm:w-72 bg-white rounded-2xl shadow-2xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 origin-bottom-right">
            <h4 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
              <span className="text-blue-500"><FaEnvelope size={18} /></span> Send Email
            </h4>
            <p className="text-xs text-gray-500 mb-2">
              Let us know who you are and how we can assist you?
            </p>
            <textarea
              value={emailMsg}
              onChange={(e) => setEmailMsg(e.target.value)}
              placeholder="Hi, my name is... I'd like to know about..."
              rows="3"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
            />
            <button
              onClick={handleEmailSend}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded-lg mt-2 transition text-sm"
            >
              Send Email
            </button>
          </div>
        </div>

        {/* Call */}
        <div className={`relative group ${isExpanded ? '' : 'sm:right-6 sm:-mr-10 -mt-8 sm:mt-0'}`}>
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center text-white text-2xl shadow-xl hover:scale-110 transition-transform duration-200 cursor-pointer">
            <FaPhoneAlt size={24} />
          </div>
          <div className="absolute bottom-full right-10 sm:right-0 mb-0 sm:mb-2 w-64 sm:w-72 bg-white rounded-2xl shadow-2xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 origin-bottom-right">
            <h4 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
              <span className="text-orange-500"><FaPhoneAlt size={18} /></span> Call Us
            </h4>
            <p className="text-xs text-gray-500 mb-2">
              Speak directly with our team over the phone.
            </p>
            <div className="bg-gray-50 rounded-lg p-2 text-center mb-3 border border-gray-100">
              <span className="text-sm font-mono font-bold text-gray-700">
                {displayPhone}
              </span>
            </div>
            <a
              href={`tel:${businessPhone}`}
              className="w-full bg-green-500 hover:bg-orange-600 text-white font-semibold py-2 rounded-lg transition text-sm flex items-center justify-center gap-2"
            >
              📞 Call Now
            </a>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      <EmailProviderModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setPendingMessage("");
        }}
        onSend={handleEmailProviderSend}
        loading={loading}
      />
    </>
  );
};

export default FloatingContactButtons;